import { parseEmailSignature, type EmailSignature } from "./email-signature";
import { INVOICE_TERM_DAYS, type InvoiceRelativeTerm } from "./invoice-terms";

export interface DocumentDefaults {
  invoicePaymentTerms: InvoiceRelativeTerm;
  invoicePaymentInstructions: string;
  invoiceNotes: string;
  estimateTerms: string;
  estimateNotes: string;
}
export interface EmailTemplate { subject: string | null; message: string | null }
export interface EmailMessages { invoice: EmailTemplate; estimate: EmailTemplate; signature?: EmailSignature | null }
export interface WorkspacePreferences { version: number; documentDefaults: DocumentDefaults; emailMessages: EmailMessages }
export type PreferenceSection = "documentDefaults" | "emailMessages";
export const PREFERENCES_SAVED_EVENT = "signalbill:workspace-preferences-saved";
export const TEMPLATE_VARIABLES = ["document_number", "document_type", "business_name", "client_name", "total", "greeting"] as const;
/** Canonical plain-text defaults; null persisted fields continue to select these. */
export const DEFAULT_EMAIL_TEMPLATES: EmailMessages = {
  invoice: {
    subject: "{{ business_name }} {{ document_type }}: {{ document_number }}",
    message: "{{ greeting }}\n\nAttached is invoice {{ document_number }} for {{ total }}.\n\nThank you for your business.",
  },
  estimate: {
    subject: "{{ business_name }} {{ document_type }}: {{ document_number }} for {{ client_name }}",
    message: "Hi {{ client_name }},\n\nAttached is estimate {{ document_number }} for {{ total }} for your review.\n\nThank you for considering {{ business_name }}.",
  },
};
const variablePattern = /\{\{\s*([a-z_]+)\s*\}\}/g;
export function emailVariableToken(name: typeof TEMPLATE_VARIABLES[number]) {
  if (!(TEMPLATE_VARIABLES as readonly string[]).includes(name)) throw new PreferenceInputError(`Unsupported template variable: ${name}.`);
  return `{{ ${name} }}`;
}
export function initialWorkspacePreferences(): WorkspacePreferences {
  return { version: 0, documentDefaults: { invoicePaymentTerms: "Net 30", invoicePaymentInstructions: "", invoiceNotes: "", estimateTerms: "", estimateNotes: "" }, emailMessages: { invoice: { subject: null, message: null }, estimate: { subject: null, message: null } } };
}
export class PreferenceInputError extends Error {}
function object(value: unknown, keys: readonly string[]): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value) || Object.keys(value).sort().join() !== [...keys].sort().join()) throw new PreferenceInputError("Use the supported preference fields.");
  return value as Record<string, unknown>;
}
function text(value: unknown, subject = false): string {
  if (typeof value !== "string" || value.length > (subject ? 200 : 2000) || (subject ? /[\u0000-\u001f\u007f]/ : /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/).test(value)) throw new PreferenceInputError(subject ? "Subjects must be one line, up to 200 characters." : "Text must be up to 2,000 characters, without unsupported control characters.");
  return value.replace(/\r\n?/g, "\n");
}
function templateField(value: unknown, subject = false): string | null {
  if (value === null) return null;
  const parsed = text(value, subject);
  if (!parsed.trim()) return null; // Empty fields restore the generated fallback.
  const remainder = parsed.replace(variablePattern, (_, name) => {
    if (!(TEMPLATE_VARIABLES as readonly string[]).includes(name)) throw new PreferenceInputError(`Unsupported template variable: ${name}.`);
    return "";
  });
  if (/\{\{|\}\}/.test(remainder)) throw new PreferenceInputError("Use supported variables in double braces, such as {{ document_number }}.");
  return parsed;
}
export function parseDocumentDefaults(value: unknown): DocumentDefaults {
  const input = object(value, Object.keys(initialWorkspacePreferences().documentDefaults));
  if (typeof input.invoicePaymentTerms !== "string" || !Object.hasOwn(INVOICE_TERM_DAYS, input.invoicePaymentTerms)) throw new PreferenceInputError("Choose an available relative payment term.");
  return { invoicePaymentTerms: input.invoicePaymentTerms as InvoiceRelativeTerm, invoicePaymentInstructions: text(input.invoicePaymentInstructions), invoiceNotes: text(input.invoiceNotes), estimateTerms: text(input.estimateTerms), estimateNotes: text(input.estimateNotes) };
}
export function parseEmailMessages(value: unknown): EmailMessages {
  const hasSignature=Boolean(value && typeof value === "object" && Object.hasOwn(value,"signature"));
  const input = object(value, ["invoice", "estimate", ...(hasSignature?["signature"]:[])]);
  const read = (value: unknown): EmailTemplate => { const t = object(value, ["subject", "message"]); return { subject: templateField(t.subject, true), message: templateField(t.message) }; };
  let signature:EmailSignature|null|undefined;
  if(hasSignature){try{signature=parseEmailSignature(input.signature);}catch(error){throw new PreferenceInputError(error instanceof Error?error.message:"Invalid signature.");}}
  return { invoice: read(input.invoice), estimate: read(input.estimate), ...(hasSignature?{signature}: {}) };
}
export function parsePreferenceUpdate(value: unknown) {
  const input = object(value, ["section", "version", "value"]);
  if (!Number.isSafeInteger(input.version) || Number(input.version) < 0) throw new PreferenceInputError("A current preference version is required.");
  if (input.section !== "documentDefaults" && input.section !== "emailMessages") throw new PreferenceInputError("Unknown preference section.");
  return { section: input.section, version: input.version as number, value: input.section === "documentDefaults" ? parseDocumentDefaults(input.value) : parseEmailMessages(input.value) };
}
export function renderEmailTemplate(template: EmailTemplate, fallback: { subject: string; message: string }, variables: Record<typeof TEMPLATE_VARIABLES[number], string>) {
  const render = (pattern: string | null, generated: string, subject: boolean) => {
    const validated = templateField(pattern, subject);
    return validated === null ? generated : text(validated.replace(variablePattern, (_, name: typeof TEMPLATE_VARIABLES[number]) => subject ? variables[name].replace(/[\r\n\t]/g, " ") : variables[name]), subject);
  };
  return { subject: render(template.subject, fallback.subject, true), message: render(template.message, fallback.message, false) };
}
